import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Auth helpers
export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  return { user, error };
};

export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  return { session, error };
};

// Profile helpers
export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  return { data, error };
};

export const updateProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();
  return { data, error };
};

export const upsertProfile = async (profile) => {
  const { data, error } = await supabase
    .from('profiles')
    .upsert(profile, { onConflict: 'id' })
    .select()
    .single();
  return { data, error };
};

// Todo helpers
export const getTodos = async (limit = 20, offset = 0) => {
  const { data, error } = await supabase
    .from('todos')
    .select(`
      *,
      profiles:user_id (
        id,
        full_name,
        avatar_url
      )
    `)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  return { data, error };
};

export const getTodosByDate = async (date) => {
  const { data, error } = await supabase
    .from('todos')
    .select(`
      *,
      profiles:user_id (
        id,
        full_name,
        avatar_url
      )
    `)
    .eq('todo_date', date)
    .order('created_at', { ascending: false });
  return { data, error };
};

export const getUserTodos = async (userId) => {
  const { data, error } = await supabase
    .from('todos')
    .select(`
      *,
      profiles:user_id (
        id,
        full_name,
        avatar_url
      )
    `)
    .eq('user_id', userId)
    .order('todo_date', { ascending: false });
  return { data, error };
};

export const createTodo = async (todo) => {
  const { data, error } = await supabase
    .from('todos')
    .insert([todo])
    .select()
    .single();
  return { data, error };
};

export const updateTodo = async (id, updates) => {
  const { data, error } = await supabase
    .from('todos')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
};

export const checkTodoExistsForDate = async (userId, date) => {
  const { data, error } = await supabase
    .from('todos')
    .select('id')
    .eq('user_id', userId)
    .eq('todo_date', date)
    .maybeSingle();
  return { data, error };
};

// Vote helpers
export const getVote = async (todoId, userId) => {
  const { data, error } = await supabase
    .from('votes')
    .select('vote_type')
    .eq('todo_id', todoId)
    .eq('user_id', userId)
    .maybeSingle();
  return { data, error };
};

export const upsertVote = async (todoId, userId, voteType) => {
  const { data, error } = await supabase
    .from('votes')
    .upsert(
      { todo_id: todoId, user_id: userId, vote_type: voteType },
      { onConflict: 'todo_id,user_id' }
    )
    .select()
    .single();
  return { data, error };
};

export const deleteVote = async (todoId, userId) => {
  const { error } = await supabase
    .from('votes')
    .delete()
    .eq('todo_id', todoId)
    .eq('user_id', userId);
  return { error };
};

export const getTodoVotes = async (todoId) => {
  const { data, error } = await supabase
    .from('votes')
    .select('vote_type')
    .eq('todo_id', todoId);
  return { data, error };
};

// Library helpers
export const getLibraryUsers = async () => {
  const { data, error } = await supabase
    .from('library')
    .select(`
      *,
      profiles:user_id (
        id,
        full_name,
        avatar_url
      )
    `)
    .eq('is_active', true)
    .order('updated_at', { ascending: false });
  return { data, error };
};

export const upsertLibraryEntry = async (entry) => {
  const { data, error } = await supabase
    .from('library')
    .upsert(entry, { onConflict: 'user_id,date' })
    .select()
    .single();
  return { data, error };
};

export const updateLibraryEntry = async (userId, updates) => {
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('library')
    .update(updates)
    .eq('user_id', userId)
    .eq('date', today)
    .select()
    .single();
  return { data, error };
};

export const getLibraryEntry = async (userId) => {
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('library')
    .select('*')
    .eq('user_id', userId)
    .eq('date', today)
    .maybeSingle();
  return { data, error };
};

// Announcement helpers
export const getAnnouncements = async (limit = 10) => {
  const { data, error } = await supabase
    .from('announcements')
    .select(`
      *,
      profiles:created_by (
        id,
        full_name,
        avatar_url
      )
    `)
    .order('created_at', { ascending: false })
    .limit(limit);
  return { data, error };
};

export const getAnnouncementById = async (id) => {
  const { data, error } = await supabase
    .from('announcements')
    .select(`
      *,
      profiles:created_by (
        id,
        full_name,
        avatar_url
      )
    `)
    .eq('id', id)
    .single();
  return { data, error };
};

export const createAnnouncement = async (announcement) => {
  const { data, error } = await supabase
    .from('announcements')
    .insert([announcement])
    .select()
    .single();
  return { data, error };
};

// Notification helpers
export const getNotifications = async (userId) => {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  return { data, error };
};

export const createNotification = async (notification) => {
  const { data, error } = await supabase
    .from('notifications')
    .insert([notification])
    .select()
    .single();
  return { data, error };
};

export const markNotificationRead = async (id) => {
  const { data, error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
};
